import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { ApiError } from '@/lib/api/errors';
import { getActiveTenantId } from '@/lib/activeTenant';
import { useContactImportStore } from '@/stores/contactImport.store';
import type { ContactImportJobView, ImportContactsDto } from '@/types/contact';

const POLL_INTERVAL_MS = 1_500;

const isFinished = (view: ContactImportJobView | undefined) =>
  view?.status === 'done' || view?.status === 'failed';

/**
 * The server no longer has the job — results are kept a day — or it is not
 * this tenant's (answered the same way). Any other error is a blip (a 500, a
 * timeout, the network) and polling carries on.
 */
const isGone = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * Queues a CSV import. The server writes the rows in the background — paced,
 * one import at a time, paused while the system is busy — and answers at once.
 * The job is tracked from then on, whether or not the dialog stays open.
 */
export function useStartContactImport() {
  const track = useContactImportStore((state) => state.track);
  return useApiMutation<ContactImportJobView, ImportContactsDto>({
    mutationFn: (dto) =>
      apiPost<ContactImportJobView, ImportContactsDto>(
        operationsClient,
        '/contacts/import/jobs',
        dto,
      ),
    onSuccess: (queued) => track(queued.jobId, getActiveTenantId()),
  });
}

/** The job's progress, polled until it finishes. Display only: no side effects. */
export function useContactImportProgress(jobId: string | null) {
  return useQuery({
    queryKey: contactKeys.importJob(jobId ?? ''),
    queryFn: () => apiGet<ContactImportJobView>(operationsClient, `/contacts/import/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (q) =>
      isFinished(q.state.data) || isGone(q.state.error) ? false : POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
}

/** The tracked import for this tenant, if one is running. */
export function useActiveContactImportJobId(): string | null {
  const active = useContactImportStore((state) => state.active);
  return active && active.tenantId === getActiveTenantId() ? active.jobId : null;
}

/**
 * Follows the tracked import to the end — mounted on the contacts page, so it
 * keeps going after the dialog closes — then refreshes the contact list,
 * reports the outcome once and stops tracking it.
 */
export function useContactImportTracker() {
  const queryClient = useQueryClient();
  const finish = useContactImportStore((state) => state.finish);
  const jobId = useActiveContactImportJobId();
  const { data: view, error } = useContactImportProgress(jobId);
  const status = view?.status;

  useEffect(() => {
    if (!jobId) return;
    if (isGone(error)) {
      finish(jobId);
      return;
    }
    if (!view || view.jobId !== jobId) return;
    if (status === 'done') {
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      const parts = [`Imported ${view.created} contacts`];
      if (view.skipped > 0) parts.push(`${view.skipped} skipped`);
      if (view.failed > 0) parts.push(`${view.failed} failed`);
      toast.success(parts.join(', '));
      finish(jobId);
    } else if (status === 'failed') {
      // Rows written before it stopped are kept; show them in the list.
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.error(view.reason ?? 'The import stopped.');
      finish(jobId);
    }
    // Once per finished job, not on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, status, error]);
}
